import { apiClient as api } from './api';
import { callV3, type ApiResponse } from '../api/envelope';
import type {
  BatchExecuteRequest,
  BatchOperationResponse,
  ColorCorrectRequest,
  ColorGradeRequest,
  ColorizeRequest,
  ConsistencySheetRequest,
  DeblurRequest,
  DenoiseRequest,
  EnhanceRequest,
  ExecuteOperationRequest,
  FaceEnhanceRequest,
  FaceSwapRequest,
  GenerateRequest,
  ImageLabOperationStatus,
  ImageToImageRequest,
  InpaintRequest,
  MultiAngleRequest,
  OperationDefinition,
  OperationResult,
  OperationStatusResponse,
  OperationsResponse,
  OutpaintRequest,
  ProvidersResponse,
  RemoveBackgroundRequest,
  RemoveObjectRequest,
  SketchToImageRequest,
  StyleTransferRequest,
  TryOnRequest,
  UpscaleRequest,
  VariationsRequest,
  ZoomRequest,
} from '../domain';

const BASE = '/api/v3/operations';

const TERMINAL: readonly ImageLabOperationStatus[] = ['Completed', 'Failed', 'Cancelled'];

export const OperationService = {
  getProviders: () =>
    callV3<ProvidersResponse>(api.get<ApiResponse<ProvidersResponse>>(`${BASE}/providers`)),

  getCatalog: () =>
    callV3<OperationsResponse>(api.get<ApiResponse<OperationsResponse>>(`${BASE}/catalog`)),

  getOperationDetails: (operationType: string) =>
    callV3<OperationDefinition>(
      api.get<ApiResponse<OperationDefinition>>(`${BASE}/catalog/${operationType}`),
    ),

  execute: (request: ExecuteOperationRequest) =>
    callV3<OperationResult>(api.post<ApiResponse<OperationResult>>(`${BASE}/execute`, request)),

  generate: (request: GenerateRequest) =>
    callV3<OperationResult>(api.post<ApiResponse<OperationResult>>(`${BASE}/generate`, request)),

  inpaint: (request: InpaintRequest) =>
    callV3<OperationResult>(api.post<ApiResponse<OperationResult>>(`${BASE}/inpaint`, request)),

  outpaint: (request: OutpaintRequest) =>
    callV3<OperationResult>(api.post<ApiResponse<OperationResult>>(`${BASE}/outpaint`, request)),

  imageToImage: (request: ImageToImageRequest) =>
    callV3<OperationResult>(
      api.post<ApiResponse<OperationResult>>(`${BASE}/image-to-image`, request),
    ),

  styleTransfer: (request: StyleTransferRequest) =>
    callV3<OperationResult>(
      api.post<ApiResponse<OperationResult>>(`${BASE}/style-transfer`, request),
    ),

  variations: (request: VariationsRequest) =>
    callV3<OperationResult>(
      api.post<ApiResponse<OperationResult>>(`${BASE}/variations`, request),
    ),

  sketchToImage: (request: SketchToImageRequest) =>
    callV3<OperationResult>(
      api.post<ApiResponse<OperationResult>>(`${BASE}/sketch-to-image`, request),
    ),

  tryOn: (request: TryOnRequest) =>
    callV3<OperationResult>(api.post<ApiResponse<OperationResult>>(`${BASE}/try-on`, request)),

  multiAngle: (request: MultiAngleRequest) =>
    callV3<OperationResult>(
      api.post<ApiResponse<OperationResult>>(`${BASE}/multi-angle`, request),
    ),

  consistencySheet: (request: ConsistencySheetRequest) =>
    callV3<OperationResult>(
      api.post<ApiResponse<OperationResult>>(`${BASE}/consistency-sheet`, request),
    ),

  removeBackground: (request: RemoveBackgroundRequest) =>
    callV3<OperationResult>(
      api.post<ApiResponse<OperationResult>>(`${BASE}/remove-background`, request),
    ),

  removeObject: (request: RemoveObjectRequest) =>
    callV3<OperationResult>(
      api.post<ApiResponse<OperationResult>>(`${BASE}/remove-object`, request),
    ),

  upscale: (request: UpscaleRequest) =>
    callV3<OperationResult>(api.post<ApiResponse<OperationResult>>(`${BASE}/upscale`, request)),

  enhance: (request: EnhanceRequest) =>
    callV3<OperationResult>(api.post<ApiResponse<OperationResult>>(`${BASE}/enhance`, request)),

  faceEnhance: (request: FaceEnhanceRequest) =>
    callV3<OperationResult>(
      api.post<ApiResponse<OperationResult>>(`${BASE}/face-enhance`, request),
    ),

  faceSwap: (request: FaceSwapRequest) =>
    callV3<OperationResult>(api.post<ApiResponse<OperationResult>>(`${BASE}/face-swap`, request)),

  denoise: (request: DenoiseRequest) =>
    callV3<OperationResult>(api.post<ApiResponse<OperationResult>>(`${BASE}/denoise`, request)),

  deblur: (request: DeblurRequest) =>
    callV3<OperationResult>(api.post<ApiResponse<OperationResult>>(`${BASE}/deblur`, request)),

  zoom: (request: ZoomRequest) =>
    callV3<OperationResult>(api.post<ApiResponse<OperationResult>>(`${BASE}/zoom`, request)),

  colorGrade: (request: ColorGradeRequest) =>
    callV3<OperationResult>(
      api.post<ApiResponse<OperationResult>>(`${BASE}/color-grade`, request),
    ),

  colorCorrect: (request: ColorCorrectRequest) =>
    callV3<OperationResult>(
      api.post<ApiResponse<OperationResult>>(`${BASE}/color-correct`, request),
    ),

  // ImageOperationsController has no /colorize endpoint — route through the
  // generic dispatcher with kind=Colorize (see ImageLabService.ExecuteOperationAsync).
  colorize: (request: ColorizeRequest) =>
    callV3<OperationResult>(
      api.post<ApiResponse<OperationResult>>(`${BASE}/execute`, {
        kind: 'Colorize',
        ...request,
      }),
    ),

  getOperationStatus: (operationId: string) =>
    callV3<OperationStatusResponse>(
      api.get<ApiResponse<OperationStatusResponse>>(`${BASE}/jobs/${operationId}`),
    ),

  cancelOperation: async (operationId: string): Promise<void> => {
    await callV3<unknown>(
      api.post<ApiResponse<unknown>>(`${BASE}/jobs/${operationId}/cancel`),
    );
  },

  async pollOperationStatus(
    operationId: string,
    onProgress?: (status: OperationStatusResponse) => void,
    maxAttempts = 120,
    intervalMs = 2000,
  ): Promise<OperationStatusResponse> {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const status = await this.getOperationStatus(operationId);
      onProgress?.(status);
      if (TERMINAL.includes(status.status)) {
        return status;
      }
      await new Promise(resolve => setTimeout(resolve, intervalMs));
    }
    throw new Error(
      `Operation '${operationId}' polling timed out after ${maxAttempts * intervalMs}ms`,
    );
  },

  batchExecute: (request: BatchExecuteRequest) =>
    callV3<BatchOperationResponse>(
      api.post<ApiResponse<BatchOperationResponse>>(`${BASE}/batch`, request),
    ),

  getBatchStatus: (batchId: string) =>
    callV3<BatchOperationResponse>(
      api.get<ApiResponse<BatchOperationResponse>>(`${BASE}/batch/${batchId}`),
    ),
};
